import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { X, ChevronDown, ChevronUp, Cpu, Wrench, FileInput, FileOutput } from "lucide-react";
import type { AgentDefinition } from "@/data/transparency";
import { getAgentIcon } from "@/data/transparency";

interface AgentDetailModalProps {
  agent: AgentDefinition;
  onClose: () => void;
}

export default function AgentDetailModal({ agent, onClose }: AgentDetailModalProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const Icon = getAgentIcon(agent.icon);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const modalContent = (
    <AnimatePresence>
      <div 
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        {/* Backdrop */}
        <motion.div 
          className="absolute inset-0 bg-black/50 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/20 shadow-2xl"
          style={{ 
            background: 'linear-gradient(to bottom right, #063F6B, #042a4d)',
            zIndex: 10000 
          }}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div 
            className="sticky top-0 p-6 border-b border-white/10 z-10"
            style={{
              background: `linear-gradient(135deg, ${agent.color}20 0%, #063F6B 100%), #063F6B`,
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${agent.color}25` }}
                >
                  <Icon 
                    className="w-7 h-7" 
                    style={{ color: agent.color }}
                  />
                </div>
                <div>
                  <span 
                    className="text-xs font-medium uppercase tracking-wider"
                    style={{ color: agent.color }}
                  >
                    {agent.subtitle}
                  </span>
                  <h2 className="text-2xl font-bold text-white">
                    {agent.name} Agent
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 rounded-lg hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* What It Does */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-500/20">
                  <Wrench className="w-4 h-4 text-amber-400" />
                </span>
                What This Agent Does
              </h3>
              <ul className="space-y-3">
                {agent.whatItDoes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span 
                      className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: agent.color }}
                    />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Data Flow */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <FileInput className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-white">Input</span>
                </div>
                <p className="text-sm text-slate-400">{agent.receives}</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <FileOutput className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-white">Output</span>
                </div>
                <p className="text-sm text-slate-400">{agent.outputs}</p>
              </div>
            </section>

            {/* Model & Tools */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-500/20">
                  <Cpu className="w-4 h-4 text-amber-400" />
                </span>
                Technology Used
              </h3>
              
              {agent.model ? (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-sm font-semibold text-white">AI Model: {agent.model}</span>
                  </div>
                  <p className="text-sm text-slate-400 pl-4">
                    {agent.modelDescription}
                  </p>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
                  <p className="text-sm text-slate-400">
                    This agent doesn't use an AI model. It relies on specialized tools for data collection.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {agent.tools.map((tool) => (
                  <span 
                    key={tool}
                    className="px-3 py-1.5 rounded-lg text-sm bg-white/10 text-slate-300 border border-white/10"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </section>

            {/* Prompt Section */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4">
                AI Instructions (Prompt)
              </h3>
              
              <p className="text-slate-400 mb-4">
                {agent.promptSummary}
              </p>

              {agent.prompt && (
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setShowPrompt(!showPrompt)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <span className="text-sm font-medium text-white">
                      {showPrompt ? "Hide Full Prompt" : "View Full Prompt"}
                    </span>
                    {showPrompt ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {showPrompt && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 bg-black/30 border-t border-white/10">
                          <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono leading-relaxed">
                            {agent.prompt}
                          </pre>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </section>
          </div>

          {/* Footer */}
          <div 
            className="sticky bottom-0 p-6 border-t border-white/10"
            style={{ background: '#042a4d' }}
          >
            <button 
              onClick={onClose}
              className="w-full px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium transition-colors border border-white/10"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  // Use portal to render modal at the root of the document body
  return createPortal(modalContent, document.body);
}
